import React, { useState, useEffect, useRef, useContext } from 'react';
import { makeTwitterSearchLink, cleanCorporateName } from '../functions/TwitterSearch';
import TuneIcon from '@mui/icons-material/Tune';
import SearchIcon from '@mui/icons-material/Search';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkAddedIcon from '@mui/icons-material/BookmarkAdded';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import supabase from '../components/supabaseClient';
import { useAuth } from '../components/AuthContext';
import { ModalContext } from '../App';
import XAdvancedOptions from './XAdvancedOptions';
import './XTool.css';

const XSearchSection = ({ savedSearch, onViewSavedSearches, isMobile, isLoggedIn, onSaveSearch }) => {
    // Search state
    const [searchTerm, setSearchTerm] = useState('');
    const [twitterLink, setTwitterLink] = useState('');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);
    
    // Time filter state
    const [timeDropdownText, setTimeDropdownText] = useState('All Time');
    const [timeValue, setTimeValue] = useState(0);
    const [dateMin, setDateMin] = useState(null);
    const [dateMax, setDateMax] = useState(null);
    
    // Advanced filter state
    const [blockHashtags, setBlockHashtags] = useState(true);
    const [blockLinks, setBlockLinks] = useState(false);
    const [onlyPopularPosts, setOnlyPopularPosts] = useState(true);
    const [minFaves, setMinFaves] = useState(0);
    const [banList, setBanList] = useState([]);
    const [banListString, setBanListString] = useState('');
    const [showNameModal, setShowNameModal] = useState(false);
    const [searchName, setSearchName] = useState('');
    
    // Saved search state
    const [isSaved, setIsSaved] = useState(false);
    const [searches, setSearches] = useState([]);
    const [activeSavedSearchId, setActiveSavedSearchId] = useState(null);
    const [searchDetails, setSearchDetails] = useState(null);
    const [savedSearchDetails, setSavedSearchDetails] = useState(null);
    const [searchHasBeenChangedVsSaved, setSearchHasBeenChangedVsSaved] = useState(false);
    
    const { userId } = useAuth();
    const modalContext = useContext(ModalContext);
    const inputRef = useRef(null);

    // Initialize from saved search if provided
    useEffect(() => {
        if (savedSearch) {
            destructSearchParams(savedSearch);
        }
    }, [savedSearch]);

    // Fetch saved searches on mount if logged in
    useEffect(() => {
        if (userId) {
            fetchSearches();
        }
    }, [userId]);

    // Generate Twitter search link whenever relevant params change
    useEffect(() => {
        const link = makeLink();
        setTwitterLink(link);
        
        // Construct search details for comparison with saved search
        const details = constructSearchDetails(link);
        setSearchDetails(details);
        
        // Compare with saved search if applicable
        if (savedSearchDetails) {
            setSearchHasBeenChangedVsSaved(areJsonTreesDifferent(savedSearchDetails, details));
        }
    }, [searchTerm, timeValue, dateMin, dateMax, blockHashtags, blockLinks, onlyPopularPosts, minFaves, banList]);

    // Update time dropdown text when time value changes
    useEffect(() => {
        switch (timeValue) {
            case 0:
                setTimeDropdownText('All Time');
                setDateMin(null);
                break;
            case 1:
                setTimeDropdownText('Past Day');
                break;
            case 7:
                setTimeDropdownText('Past Week');
                break;
            case 30:
                setTimeDropdownText('Past Month');
                break;
            case 365:
                setTimeDropdownText('Past Year');
                break;
            default:
                setTimeDropdownText('Custom Range');
                break;
        }
    }, [timeValue]);

    // Handle search submission
    const handleSearch = () => {
        if (twitterLink) {
            logSearch();
            window.open(twitterLink, '_blank');
        }
    };

    // Generate search link with all params
    function makeLink() {
        let effectiveDateMin = dateMin;
        let effectiveDateMax = dateMax;

        if (timeValue > 0) {
            const todayDate = new Date();
            const newDate = new Date(todayDate);
            newDate.setDate(newDate.getDate() - timeValue);
            const newDateString = newDate.toISOString().split('T')[0];
            effectiveDateMin = newDateString;
            effectiveDateMax = null;
        }

        // Ensure banList only contains valid strings
        const validBanList = banList
            .filter(item => item !== null && item !== undefined)
            .map(item => String(item).trim())
            .filter(item => item.length > 0);

        return makeTwitterSearchLink({
            query: searchTerm,
            dateMin: effectiveDateMin,
            dateMax: effectiveDateMax,
            blockHashtags: blockHashtags,
            blockLinks: blockLinks,
            onlyPopularPosts: onlyPopularPosts,
            minFaves: minFaves,
            banList: validBanList
        });
    }

    // Log search to analytics (if needed)
    const logSearch = () => {
        // Can be implemented if analytics are needed
    };

    // Handle Enter key in search input
    const onEnter = (event) => {
        if (event.key === 'Enter' && isInputFocused) {
            handleSearch();
        }
    };

    // Toggle advanced options
    const toggleAdvanced = () => {
        setShowAdvanced(!showAdvanced);
    };

    // Fetch saved searches from supabase
    const fetchSearches = async () => {
        if (!userId) {
            console.log("No userId available for fetchSearches");
            return;
        }
        
        console.log("Fetching saved searches in XSearchSection for user:", userId);
        
        try {
            const { data, error } = await supabase
                .from('saved_searches')
                .select('*')
                .eq('user_id', userId)
                .eq('type', 'twitter');
                
            if (error) {
                console.error('Error in fetchSearches XSearchSection:', error);
                throw error;
            }
            
            console.log("XSearchSection saved searches received:", data);
            
            if (data) {
                console.log(`XSearchSection found ${data.length} saved searches`);
                setSearches(data);
            } else {
                console.log("XSearchSection: No saved searches found");
            }
        } catch (error) {
            console.error('Error fetching searches in XSearchSection:', error);
        }
    };

    // Construct search parameters object
    const constructSearchParams = () => {
        return {
            query: searchTerm,
            dateMin,
            dateMax,
            blockHashtags,
            blockLinks,
            onlyPopularPosts,
            minFaves,
            banList,
            timeValue
        };
    };

    // Construct complete search details object
    const constructSearchDetails = (link) => {
        return {
            id: activeSavedSearchId || null,
            params: constructSearchParams(),
            link: link,
            name: searchName || 'Unnamed Search'
        };
    };

    // Destructure search parameters from saved search
    const destructSearchParams = (data) => {
        if (!data) return;
        
        const params = data.params || {};
        setSearchTerm(params.query || '');
        setDateMin(params.dateMin || null);
        setDateMax(params.dateMax || null);
        setBlockHashtags(params.blockHashtags !== undefined ? params.blockHashtags : true);
        setBlockLinks(params.blockLinks || false);
        setOnlyPopularPosts(params.onlyPopularPosts !== undefined ? params.onlyPopularPosts : true);
        setMinFaves(params.minFaves || 0);
        setBanList(params.banList || []);
        setBanListString(params.banList ? params.banList.join(', ') : '');
        setTimeValue(params.timeValue || 0);
        
        setActiveSavedSearchId(data.id);
        setSearchName(data.name || '');
        setSavedSearchDetails(data);
    };

    // Save or update search
    const saveSearch = async (name) => {
        if (!userId) {
            console.log("No userId available for saveSearch");
            return;
        }
        
        const details = constructSearchDetails(twitterLink);
        details.name = name;
        
        console.log("Saving search with details:", details);
        
        try {
            let response;
            
            const saveData = {
                search_params: details.params,
                name: details.name,
                link: details.link,
                user_id: userId,
                type: 'twitter'
            };
            
            console.log("Data to save:", saveData);
            
            if (activeSavedSearchId) {
                console.log("Updating existing search:", activeSavedSearchId);
                // Update existing search
                response = await supabase
                    .from('saved_searches')
                    .update(saveData)
                    .eq('id', activeSavedSearchId);
            } else {
                console.log("Creating new search");
                // Create new search
                response = await supabase
                    .from('saved_searches')
                    .insert([saveData]);
            }
            
            if (response.error) {
                console.error('Supabase error:', response.error);
                throw response.error;
            }
            
            console.log("Save search response:", response);
            
            // Update saved search details and status
            setSavedSearchDetails(details);
            setIsSaved(true);
            setSearchHasBeenChangedVsSaved(false);
            fetchSearches();
            
            // Call the onSaveSearch callback to update parent component
            if (onSaveSearch) {
                console.log("Calling onSaveSearch callback");
                onSaveSearch();
            }
            
        } catch (error) {
            console.error('Error saving search:', error);
            // Add additional error handling here if needed
            if (error.message) {
                alert(`Failed to save search: ${error.message}`);
            }
        }
    };

    // Handle save button click
    const handleSaveClick = () => {
        if (!userId) return;
        
        if (searchName) {
            saveSearch(searchName);
        } else {
            setShowNameModal(true);
        }
    };

    // Handle saving with a name
    const handleSaveName = () => {
        if (searchName.trim()) {
            saveSearch(searchName);
            setShowNameModal(false);
        }
    };

    // Compare two JSON objects to detect changes
    function areJsonTreesDifferent(obj1, obj2) {
        if (!obj1 || !obj2) return true;
        
        // Compare simple values
        function compareValues(value1, value2) {
            if (value1 === value2) return false;
            if (value1 === null || value2 === null) return true;
            
            return JSON.stringify(value1) !== JSON.stringify(value2);
        }
        
        // Compare objects
        function compareObjects(obj1, obj2) {
            const keys1 = Object.keys(obj1);
            const keys2 = Object.keys(obj2);
            
            // Different number of keys
            if (keys1.length !== keys2.length) return true;
            
            // Check each key
            for (const key of keys1) {
                if (!obj2.hasOwnProperty(key)) return true;
                
                const val1 = obj1[key];
                const val2 = obj2[key];
                
                // Compare based on type
                if (typeof val1 !== typeof val2) return true;
                
                if (typeof val1 === 'object' && val1 !== null) {
                    if (Array.isArray(val1) !== Array.isArray(val2)) return true;
                    
                    if (Array.isArray(val1)) {
                        if (val1.length !== val2.length) return true;
                        
                        for (let i = 0; i < val1.length; i++) {
                            if (compareValues(val1[i], val2[i])) return true;
                        }
                    } else {
                        if (compareObjects(val1, val2)) return true;
                    }
                } else {
                    if (compareValues(val1, val2)) return true;
                }
            }
            
            return false;
        }
        
        return compareObjects(obj1, obj2);
    }

    // Handle blacklist input change
    const handleBanListChange = (e) => {
        setBanListString(e.target.value);
        
        // Split by commas and clean up, ensuring all items are valid strings
        const banArray = e.target.value
            .split(',')
            .map(item => item.trim())
            .filter(item => item.length > 0);
            
        setBanList(banArray);
    };

    // Handle save button click when not logged in
    const handleSaveClickNotLoggedIn = () => {
        if (modalContext && modalContext.openLoginModal) {
            modalContext.openLoginModal(true);
        }
    };

    return (
        <div className="xtool-search-section">
            <div className="xtool-header-content">
                <h1 className="xtool-search-title">X Search Tool</h1>
                <p className="xtool-search-description">
                    Build powerful X (Twitter) searches with advanced filtering options. 
                    Find relevant discussions about stocks, companies, and market trends 
                    while filtering out spam and noise.
                </p>
            </div>
            
            <div className="xtool-search-container">
                <div className="xtool-search-input-container">
                    <input
                        ref={inputRef}
                        type="text"
                        className="xtool-search-input"
                        placeholder="Enter ticker symbol ($AAPL) or keyword search..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onFocus={() => setIsInputFocused(true)}
                        onBlur={() => setTimeout(() => setIsInputFocused(false), 200)}
                        onKeyDown={onEnter}
                    />
                    
                    <div className="xtool-search-buttons">
                        {isLoggedIn && (isSaved || activeSavedSearchId) ? (
                            <div className="xtool-save-status">
                                {searchHasBeenChangedVsSaved ? (
                                    <button className="xtool-update-button xtool-button" onClick={handleSaveClick}>
                                        <BookmarkBorderIcon />
                                        {!isMobile && <span>Update</span>}
                                    </button>
                                ) : (
                                    <div className="xtool-saved-indicator xtool-button">
                                        <BookmarkAddedIcon />
                                        {!isMobile && <span>Saved</span>}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button 
                                className="xtool-save-button xtool-button"
                                onClick={isLoggedIn ? handleSaveClick : handleSaveClickNotLoggedIn}
                                disabled={!searchTerm.trim()}
                            >
                                <BookmarkBorderIcon />
                                {!isMobile && <span>{isLoggedIn ? 'Save' : 'Save (Sign Up)'}</span>}
                            </button>
                        )}
                        
                        <button 
                            className="xtool-search-button xtool-button"
                            onClick={handleSearch}
                            disabled={!searchTerm.trim()}
                        >
                            <SearchIcon />
                            {!isMobile && <span>Search</span>}
                        </button>
                    </div>
                </div>
                
                <div className="xtool-saved-search-controls">
                    <button 
                        className="xtool-advanced-button xtool-button"
                        onClick={toggleAdvanced}
                        aria-label="Advanced options"
                    >
                        <TuneIcon />
                        <span>Advanced Options</span>
                    </button>
                    
                    {isLoggedIn && (
                        <button 
                            className="xtool-view-saved-button"
                            onClick={onViewSavedSearches}
                        >
                            View Saved Searches
                        </button>
                    )}
                </div>
                
                {showAdvanced && (
                    <XAdvancedOptions
                        timeValue={timeValue}
                        setTimeValue={setTimeValue}
                        timeDropdownText={timeDropdownText}
                        dateMin={dateMin}
                        setDateMin={setDateMin}
                        dateMax={dateMax}
                        setDateMax={setDateMax}
                        blockHashtags={blockHashtags}
                        setBlockHashtags={setBlockHashtags}
                        blockLinks={blockLinks}
                        setBlockLinks={setBlockLinks}
                        onlyPopularPosts={onlyPopularPosts}
                        setOnlyPopularPosts={setOnlyPopularPosts}
                        minFaves={minFaves}
                        setMinFaves={setMinFaves}
                        banListString={banListString}
                        handleBanListChange={handleBanListChange}
                        onClose={() => setShowAdvanced(false)}
                    />
                )}
                
                {/* Save Search Name Modal */}
                {showNameModal && (
                    <div className="xtool-modal-overlay">
                        <div className="xtool-modal">
                            <div className="xtool-modal-header">
                                <h3>Save Search</h3>
                                <button className="xtool-close-button" onClick={() => setShowNameModal(false)}>
                                    <CloseIcon />
                                </button>
                            </div>
                            <div className="xtool-modal-content">
                                <p>Give your search a name:</p>
                                <input
                                    type="text"
                                    className="xtool-input"
                                    value={searchName}
                                    onChange={(e) => setSearchName(e.target.value)}
                                    placeholder="e.g., AAPL Market Sentiment"
                                    autoFocus
                                />
                                <div className="xtool-modal-buttons">
                                    <button 
                                        className="xtool-button xtool-button-secondary"
                                        onClick={() => setShowNameModal(false)}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        className="xtool-button xtool-button-primary"
                                        onClick={handleSaveName}
                                        disabled={!searchName.trim()}
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default XSearchSection; 